import React, { useRef } from 'react';
import './ComeceAqui.css';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import { PassoDoComece, AcaoDoPasso, progresso, proximoPasso } from '../utils/comeceAqui';

/*
 * "Comece por aqui" (22/10) — docs/designs/comece-por-aqui.md.
 *
 * Duas formas da mesma lista: o modal (abre sozinho uma vez, e pelo menu do
 * avatar) e o card do painel, que mostra só o próximo passo. Nenhuma das duas
 * marca nada por clique: o passo se marca quando a coisa existe de verdade.
 */

interface ModalProps {
  nome?: string;
  passos: PassoDoComece[];
  /** Passo que precisa de um recado a mais, como "abra no celular" para o app. */
  observacao?: Partial<Record<AcaoDoPasso, string>>;
  onAcao: (acao: AcaoDoPasso) => void;
  onTour: () => void;
  onFechar: () => void;
}

export const ComeceAquiModal: React.FC<ModalProps> = ({ nome, passos, observacao = {}, onAcao, onTour, onFechar }) => {
  useTravarScroll();
  useFechavelComVoltar(true, onFechar);
  const caixa = useRef<HTMLDivElement>(null);
  useFocoNoModal(caixa);

  const primeiroNome = nome?.split(' ')[0] || '';
  const p = progresso(passos);
  const proximo = proximoPasso(passos);

  return (
    <div className="ca-fundo" role="dialog" aria-modal="true" aria-labelledby="ca-titulo" onClick={onFechar}>
      <div className="ca-caixa" ref={caixa} onClick={e => e.stopPropagation()}>
        <button className="ca-fechar" onClick={onFechar} aria-label="Fechar">×</button>

        <h2 className="ca-titulo" id="ca-titulo">
          {primeiroNome ? `${primeiroNome}, comece por aqui` : 'Comece por aqui'}
        </h2>
        <p className="ca-sub">
          {p.completo
            ? 'Tudo pronto. Agora é só ir lançando e perguntando ao Finn.'
            : <>{p.feitos} de {p.total} feitos.<span className="ca-sub-resto"> Siga na ordem: cada passo deixa o próximo mais útil.</span></>}
        </p>

        <ol className="ca-passos">
          {passos.map((passo, i) => {
            const atual = proximo?.id === passo.id;
            /*
             * Só o passo atual abre (22/10: o modal não pode rolar). Os outros
             * são uma linha: feito, o título com ✓; pendente, o título e o
             * botão à direita. O texto deles fica no `title`, para quem passa
             * o mouse.
             */
            const classe = `ca-passo${passo.feito ? ' ca-passo--feito' : ''}${atual ? ' ca-passo--atual' : ' ca-passo--linha'}`;
            return (
              <li key={passo.id} className={classe} title={!atual && !passo.feito ? passo.texto : undefined}>
                <span className="ca-num" aria-hidden="true">{passo.feito ? '✓' : i + 1}</span>
                <div className="ca-corpo">
                  <strong>
                    {passo.titulo}
                    {passo.opcional && atual && <span className="ca-opcional"> · se for o seu caso</span>}
                  </strong>
                  {atual && <span className="ca-texto">{passo.texto}</span>}
                  {atual && observacao[passo.acao] && (
                    <span className="ca-obs">{observacao[passo.acao]}</span>
                  )}
                  {passo.feito && <span className="ca-sr"> (feito)</span>}
                </div>
                {atual && (
                  <button className="ca-btn" onClick={() => onAcao(passo.acao)}>
                    {passo.botao}
                  </button>
                )}
                {/* Fechado, o botão é uma seta: com o rótulo inteiro ("Ir para
                    Nossa conta") o título espremia em quatro linhas no celular. */}
                {!atual && !passo.feito && (
                  <button className="ca-ir" onClick={() => onAcao(passo.acao)}
                          aria-label={passo.botao} title={passo.botao}>
                    →
                  </button>
                )}
              </li>
            );
          })}
        </ol>

        <button className="ca-tour" onClick={onTour}>Conhecer cada tela do app →</button>
      </div>
    </div>
  );
};

interface CardProps {
  passos: PassoDoComece[];
  onAcao: (acao: AcaoDoPasso) => void;
  onVerTudo: () => void;
  onDispensar: () => void;
}

/** O card do painel: o próximo passo e o progresso. Some quando tudo está feito. */
export const ComeceAquiCard: React.FC<CardProps> = ({ passos, onAcao, onVerTudo, onDispensar }) => {
  const p = progresso(passos);
  const proximo = proximoPasso(passos);
  if (p.completo || !proximo) return null;

  return (
    <section className="ca-card" aria-label="Comece por aqui">
      <div className="ca-card-topo">
        <span className="ca-card-rotulo">Comece por aqui · {p.feitos} de {p.total}</span>
        <button className="ca-card-x" onClick={onDispensar} aria-label="Dispensar" title="Dispensar">×</button>
      </div>
      <div className="ca-barra" aria-hidden="true">
        <span style={{ width: `${(p.feitos / p.total) * 100}%` }} />
      </div>
      <strong className="ca-card-titulo">{proximo.titulo}</strong>
      <span className="ca-texto">{proximo.texto}</span>
      <div className="ca-card-acoes">
        <button className="ca-btn" onClick={() => onAcao(proximo.acao)}>{proximo.botao}</button>
        <button className="ca-link" onClick={onVerTudo}>Ver todos os passos</button>
      </div>
    </section>
  );
};
